/**
 * Validation constants for CSV import
 * Uses enums from models for type-safe validation
 */

import { BacnetPointType } from '@shared/models/point';
import { ModbusDataType, ModbusEndianness } from '@shared/models/modbus';
import { LoRaWANPointType, LoRaWANPointFormat } from '@shared/models/lorawan';

// CSV-specific validation values (strings from CSV files)
export const BOOLEAN_VALUES = ['true', 'false', ''];

// LoRaWAN point types
export const VALID_LORAWAN_POINT_TYPES: readonly LoRaWANPointType[] = [
  LoRaWANPointType.UplinkPayload,
  LoRaWANPointType.UplinkMetadata
];

// LoRaWAN point formats
export const VALID_LORAWAN_POINT_FORMATS: readonly LoRaWANPointFormat[] = [
  LoRaWANPointFormat.Numeric,
  LoRaWANPointFormat.Boolean
];

// Alarm level names matching the order from Tile.getDefaultAnalogConditions()
export const ALARM_LEVEL_NAMES = ['Low Critical', 'Low Warning', 'High Warning', 'High Critical'];

// BACnet type categories - subsets of BacnetPointType enum
export const VALID_BACNET_NUMERIC_TYPES: readonly BacnetPointType[] = [
  BacnetPointType.AnalogValue,
  BacnetPointType.AnalogInput,
  BacnetPointType.AnalogOutput,
  BacnetPointType.MultiStateValue,
  BacnetPointType.MultiStateInput,
  BacnetPointType.MultiStateOutput
];

export const VALID_BACNET_TOGGLE_TYPES: readonly BacnetPointType[] = [
  BacnetPointType.BinaryValue,
  BacnetPointType.BinaryInput,
  BacnetPointType.BinaryOutput
];

export const VALID_BACNET_POINT_TYPES: readonly BacnetPointType[] = [
  ...VALID_BACNET_NUMERIC_TYPES,
  ...VALID_BACNET_TOGGLE_TYPES
];

// Modbus type categories - derived from ModbusDataType enum (single source of truth)
// All data types from the enum
export const VALID_MODBUS_DATA_TYPES: readonly ModbusDataType[] = Object.values(ModbusDataType) as ModbusDataType[];

// Categorize by functionality (requires domain knowledge)
export const VALID_MODBUS_TOGGLE_TYPES: readonly ModbusDataType[] = [
  ModbusDataType.Bool
];

export const VALID_MODBUS_NUMERIC_TYPES: readonly ModbusDataType[] = VALID_MODBUS_DATA_TYPES.filter(
  type => !VALID_MODBUS_TOGGLE_TYPES.includes(type)
);

// Modbus Byte Order - derived from ModbusEndianness enum (single source of truth)
// All endianness values from the enum
export const VALID_MODBUS_BYTE_ORDER: readonly ModbusEndianness[] = Object.values(ModbusEndianness) as ModbusEndianness[];

// Type guard functions for CSV validation
export function isBacnetNumericType(value: string): value is BacnetPointType {
  return (VALID_BACNET_NUMERIC_TYPES as readonly string[]).includes(value);
}

export function isBacnetToggleType(value: string): value is BacnetPointType {
  return (VALID_BACNET_TOGGLE_TYPES as readonly string[]).includes(value);
}

export function isModbusNumericType(value: string): value is ModbusDataType {
  return (VALID_MODBUS_NUMERIC_TYPES as readonly string[]).includes(value);
}

export function isLoRaWANPointType(value: string): value is LoRaWANPointType {
  return (VALID_LORAWAN_POINT_TYPES as readonly string[]).includes(value);
}

export function isLoRaWANPointFormat(value: string): value is LoRaWANPointFormat {
  return (VALID_LORAWAN_POINT_FORMATS as readonly string[]).includes(value);
}
